
import { computed, defineComponent, ref, Ref, watch } from 'vue';
import PageHeader from '@/shared/layout/page-header/PageHeader.vue';
import ApiMessages from '@/core/messages/ApiMessages.vue';
import AgentsInfoBar from '@/b2b/agents/ui/AgentsInfoBar.vue';
import Breadcrumb from '@/shared/layout/breadcrumb/Breadcrumb.vue';
import SiteLayout from '@/shared/content/SiteLayout.vue';
import CustomerServiceBar from '@/b2b/customer-service/ui/CustomerServiceBar.vue';
import bus from '@/core/bus';
import { SpaAwaitingPageEventKey, SpaPageRenderedEventKey } from '@/core/router';
import pageStore from '@/shared/content/page.store';
import gtm from '@/shared/tracking/gtm.service';
import BusinessComponent from '@/shared/layout/Business/BusinessComponent.vue';
import useSTOREBasket from '@/storecontext/basket/useBasket';
import authStore from '@/shared/auth/auth.store';
import DialogHost from '@/shared/dialog/DialogHost.vue';
import WishlistOverlay from '@/shared/product/wishlist/ui/WishlistOverlay.vue';
import FreightInfoDialog from '@/shared/product/freight-information/ui/FreightInfoDialog.vue';
import useTEMPB2CFeatureBasket from '@/b2c/basket/useTEMPB2CFeatureBasket';
import useTEMPB2BFeatureBasket from '@/b2b/basket/useTEMPB2BFeatureBasket';

const spaAwaitingPageAnimationTiming: number = 2500; // ms before dimming site on pending page change
let spaAwaitingPageAnimationTimer: number | null = null;

export default defineComponent({
    name: 'App',
    components: {
        FreightInfoDialog,
        WishlistOverlay,
        BusinessComponent,
        CustomerServiceBar,
        SiteLayout,
        Breadcrumb,
        AgentsInfoBar,
        ApiMessages,
        PageHeader,
        DialogHost,
    },
    setup() {
        const pending: Ref<boolean> = ref(false);
        const setPending = async() => {
            spaAwaitingPageAnimationTimer && clearTimeout(spaAwaitingPageAnimationTimer);
            spaAwaitingPageAnimationTimer = setTimeout(() => {
                pending.value = true;
            }, spaAwaitingPageAnimationTiming);
        };
        const clearPending = async() => {
            spaAwaitingPageAnimationTimer && clearTimeout(spaAwaitingPageAnimationTimer);
            spaAwaitingPageAnimationTimer = null;
            pending.value = false;
        };
        bus.on(SpaAwaitingPageEventKey, setPending);
        bus.on(SpaPageRenderedEventKey, clearPending);

        const shouldShowContent: Ref<boolean> = computed(() => pageStore.hasContentTiles || pageStore.hasLeftNavigation);

        const initTrackPageViewsGA4 = (): void => {
            gtm.pageViewGA4();
            bus.on(SpaPageRenderedEventKey, () => {
                gtm.pageViewGA4();
            });
        };

        const initBasketContext = async(basketId: string): Promise<void> => {
            if (!basketId) return;
            if (authStore.isSignedInAsStore) {
                const { init } = useSTOREBasket();
                await init(basketId);
            } else if (authStore.isSignedInAsPartner) {
                const { init } = useTEMPB2BFeatureBasket();
                await init(basketId);
            } else {
                const { init } = useTEMPB2CFeatureBasket();
                await init(basketId);
                initTrackPageViewsGA4();
            }
        };
        watch(() => pageStore.basketId, initBasketContext, { immediate: true });

        return {
            pending,
            shouldShowContent,
        };
    },
});

